/**
 * Tenant & Permission Registry v1 - Browser ESM Copy
 * ===================================================
 * ESM copy of the registry for Vue/Vite browser imports.
 * 
 * SOURCE OF TRUTH: functions-common/authRegistry.common.js (CJS)
 * THIS FILE: ESM copy (duplicated data, kept in sync via test)
 * 
 * KEEP IN SYNC: When updating TENANTS/ACCESS_CLASSES/FINE_FLAGS in the CJS
 * source, apply the same changes here. The test suite verifies they match.
 * 
 * FORBIDDEN: module, module.exports, require, createRequire, node:module, Buffer
 */

// ============================================================================
// TENANTS
// ============================================================================

const TENANTS = [
    {
        key: "stc",
        databaseURL: "https://stc-21-app.firebaseio.com",
        functionsSuffix: "",
    },
    {
        key: "doa",
        databaseURL: "https://doa-21-training.firebaseio.com",
        functionsSuffix: "Doa",
    },
    {
        key: "test",
        databaseURL: "https://stc-21-app-training.firebaseio.com",
        functionsSuffix: "Test",
    },
];

// ============================================================================
// ACCESS CLASSES (per-tenant booleans)
// ============================================================================

const ACCESS_CLASSES = [
    {
        key: "internal",
        description: "Internal STC employee",
    },
    {
        key: "flight_test_crew",
        description: "Flight test crew member",
    },
    {
        key: "signatory",
        description: "External signatory (e.g., for Matrices)",
    },
    {
        key: "customer",
        description: "Customer account (per-tenant, with customer_id)",
    },
];

// ============================================================================
// OPTIONAL TENANT PROPERTIES (v1)
// ============================================================================

const TENANT_PROPERTIES = [
    {
        key: "customer_id",
        type: "string",
        description: "Customer identifier (when customer=true)",
        requiredWhen: "customer",
    },
    {
        key: "signatory_military",
        type: "boolean",
        description: "Military matrix variant (property, not a fifth class)",
        requiredWhen: null,
    },
];

// ============================================================================
// FINE-GRAINED FLAGS (Security/* RTDB paths)
// ============================================================================

const FINE_FLAGS = [
    // General (7 flags)
    { category: "General", flag: "User", rulesReads: true },
    { category: "General", flag: "Admin", rulesReads: false },
    { category: "General", flag: "ApplicationAdmin", rulesReads: true },
    { category: "General", flag: "UserAdmin", rulesReads: true },
    { category: "General", flag: "Training", rulesReads: true },
    { category: "General", flag: "SafetyManagementUser", rulesReads: true },
    { category: "General", flag: "SafetyManagementManager", rulesReads: false },
    { category: "General", flag: "SafetyManagementComittee", rulesReads: false },

    // Database (7 flags)
    { category: "Database", flag: "Read", rulesReads: true },
    { category: "Database", flag: "Write", rulesReads: true },
    { category: "Database", flag: "Admin", rulesReads: false },
    { category: "Database", flag: "DrawingApprover", rulesReads: false },
    { category: "Database", flag: "DrawingChecker", rulesReads: false },
    { category: "Database", flag: "TechnicalSecretary", rulesReads: false },
    { category: "Database", flag: "Projects", rulesReads: false, uiLabel: "Projects (Commercials)" },

    // Military (3 flags)
    { category: "Military", flag: "Read", rulesReads: true },
    { category: "Military", flag: "Write", rulesReads: true },
    { category: "Military", flag: "Reviewer", rulesReads: false },

    // Audits (5 flags)
    { category: "Audits", flag: "Read", rulesReads: true },
    { category: "Audits", flag: "Write", rulesReads: true },
    { category: "Audits", flag: "Admin", rulesReads: false },
    { category: "Audits", flag: "DAM", rulesReads: false },
    { category: "Audits", flag: "HOD", rulesReads: false },

    // Claims (3 flags)
    { category: "Claims", flag: "User", rulesReads: true },
    { category: "Claims", flag: "Authoriser", rulesReads: false },
    { category: "Claims", flag: "Payer", rulesReads: false },

    // FlightTest (3 flags)
    { category: "FlightTest", flag: "Read", rulesReads: false },
    { category: "FlightTest", flag: "Write", rulesReads: false },
    { category: "FlightTest", flag: "Reviewer", rulesReads: false },

    // Leave (4 flags)
    { category: "Leave", flag: "Read", rulesReads: true },
    { category: "Leave", flag: "Write", rulesReads: true },
    { category: "Leave", flag: "Admin", rulesReads: false },
    { category: "Leave", flag: "Authoriser", rulesReads: false },

    // Matrices (4 flags)
    { category: "Matrices", flag: "Read", rulesReads: true },
    { category: "Matrices", flag: "Write", rulesReads: false },
    { category: "Matrices", flag: "Reviewer", rulesReads: true },
    { category: "Matrices", flag: "HeadOfDesign", rulesReads: false },
];

// ============================================================================
// V1 CLAIM SHAPE & HELPERS (Node-free)
// ============================================================================

function buildTenantClaim({
    internal,
    flight_test_crew,
    signatory,
    customer,
    customer_id,
    signatory_military,
}) {
    const claim = {};

    for (const [type, value] of Object.entries({ internal, flight_test_crew, signatory, customer, signatory_military })) {
        if (value !== undefined && typeof value !== "boolean") {
            throw new Error(`${type} must be a boolean`);
        }
    }

    // Add access classes (only truthy values)
    if (internal) claim.internal = true;
    if (flight_test_crew) claim.flight_test_crew = true;
    if (signatory) claim.signatory = true;
    if (customer) claim.customer = true;

    // Validate customer_id
    if (customer) {
        if (typeof customer_id !== "string" || customer_id.trim() === "") {
            throw new Error("customer_id is required when customer=true");
        }
        if (/[.#$\[\]/\u0000-\u001f\u007f]/.test(customer_id)) {
            throw new Error("customer_id must be a valid RTDB key");
        }
        claim.customer_id = customer_id.trim();
    }

    // Add optional properties
    if (signatory_military) claim.signatory_military = true;

    // At least one access class must be true
    const hasClass = internal || flight_test_crew || signatory || customer || signatory_military;
    if (!hasClass) {
        throw new Error("At least one access class must be true");
    }

    return claim;
}

function buildClaimShape({ stc, doa, test }) {
    const claim = {};

    if (stc) claim.stc = stc;
    if (doa) claim.doa = doa;
    if (test) claim.test = test;

    if (!stc && !doa && !test) {
        throw new Error("At least one tenant claim must be provided");
    }

    return claim;
}

// ============================================================================
// EXPORTS (ESM for browser/Vite)
// ============================================================================

export {
    // Core registry data
    TENANTS,
    ACCESS_CLASSES,
    TENANT_PROPERTIES,
    FINE_FLAGS,

    // Helpers (Node-free)
    buildTenantClaim,
    buildClaimShape,
};
